import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  alpha,
  useTheme,
  Paper,
  Fade,
  Zoom
} from '@mui/material';
import {
  Storage,
  Memory,
  CheckCircle,
  Error as ErrorIcon,
  AccessTime,
  Speed,
  DataObject,
  Key as KeyIcon,
  FiberManualRecord
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

function ServiceCard({ service, icon, gradient, delay = 0 }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [serviceStatus, setServiceStatus] = useState(null);

  useEffect(() => {
    fetchServiceStatus();
    const interval = setInterval(fetchServiceStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchServiceStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/services/${service}`, {
        withCredentials: true
      });
      setServiceStatus(response.data);
    } catch (error) {
      setServiceStatus({ connected: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatMemory = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  const isConnected = serviceStatus?.connected === true;

  const gradients = {
    mongo: 'linear-gradient(135deg, #4DB33D 0%, #3FA037 100%)',
    redis: 'linear-gradient(135deg, #DC382D 0%, #A41E11 100%)',
  };

  const serviceName = service === 'mongodb' ? 'MongoDB' : 'Redis';
  const serviceGradient = service === 'mongodb' ? gradients.mongo : gradients.redis;

  return (
    <Zoom in timeout={300 + delay}>
      <Box>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 4,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.1)}`,
            }
          }}
        >
          {/* Header */}
          <Box sx={{
            p: 2.5,
            background: isConnected
              ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.light, 0.05)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: serviceGradient,
                  boxShadow: `0 4px 12px ${alpha(service === 'mongodb' ? '#4DB33D' : '#DC382D', 0.3)}`
                }}>
                  {React.cloneElement(icon, { sx: { color: 'white', fontSize: 24 } })}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {serviceName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FiberManualRecord sx={{
                      fontSize: 10,
                      color: isConnected ? theme.palette.success.main : theme.palette.error.main,
                      animation: isConnected ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.5 }
                      }
                    }} />
                    <Typography variant="caption" color={isConnected ? 'success.main' : 'error.main'} fontWeight={600}>
                      {isConnected ? 'Connected' : 'Disconnected'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Chip
                icon={isConnected
                  ? <CheckCircle sx={{ fontSize: 16 }} />
                  : <ErrorIcon sx={{ fontSize: 16 }} />
                }
                label={isConnected ? 'Online' : 'Offline'}
                size="small"
                sx={{
                  fontWeight: 600,
                  background: isConnected
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.error.main, 0.1),
                  color: isConnected ? theme.palette.success.main : theme.palette.error.main,
                  border: `1px solid ${isConnected
                    ? alpha(theme.palette.success.main, 0.3)
                    : alpha(theme.palette.error.main, 0.3)}`
                }}
              />
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2.5 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: service === 'mongodb' ? '#4DB33D' : '#DC382D' }} />
              </Box>
            ) : serviceStatus?.error && !isConnected ? (
              <Box sx={{
                p: 2,
                borderRadius: 2,
                background: alpha(theme.palette.error.main, 0.05),
                border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`
              }}>
                <Typography variant="body2" color="error" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {serviceStatus.error}
                </Typography>
              </Box>
            ) : isConnected ? (
              <Fade in timeout={400}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Uptime */}
                  {serviceStatus.uptime !== undefined && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 2.5,
                      background: alpha(theme.palette.action.hover, 0.5),
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Uptime
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                          {formatUptime(serviceStatus.uptime)}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Version */}
                  {serviceStatus.version && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 2.5,
                      background: alpha(theme.palette.action.hover, 0.5),
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Speed sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Version
                          </Typography>
                        </Box>
                        <Chip
                          label={serviceStatus.version}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            background: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Memory */}
                  {serviceStatus.memory && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 2.5,
                      background: alpha(theme.palette.action.hover, 0.5),
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: serviceStatus.memoryMax ? 1.5 : 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Memory sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Memory Usage
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                          {formatMemory(serviceStatus.memory)}
                        </Typography>
                      </Box>
                      {serviceStatus.memoryMax && (
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((serviceStatus.memory / serviceStatus.memoryMax) * 100, 100)}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(service === 'mongodb' ? '#4DB33D' : '#DC382D', 0.1),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              background: serviceGradient
                            }
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Connections */}
                  {serviceStatus.connections !== undefined && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 2.5,
                      background: alpha(theme.palette.action.hover, 0.5),
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <DataObject sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Connections
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                          {serviceStatus.connections}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Databases (MongoDB) */}
                  {serviceStatus.databases !== undefined && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 2.5,
                      background: alpha(theme.palette.action.hover, 0.5),
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Storage sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Databases
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                          {serviceStatus.databases}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Keys (Redis) */}
                  {serviceStatus.keys !== undefined && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 2.5,
                      background: alpha(theme.palette.action.hover, 0.5),
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <KeyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Keys
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                          {serviceStatus.keys.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Fade>
            ) : (
              <Box sx={{
                py: 4,
                textAlign: 'center'
              }}>
                <ErrorIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Service is not available
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Zoom>
  );
}

export default function ServiceStatus() {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <ServiceCard
          service="mongodb"
          icon={<Storage />}
          delay={0}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ServiceCard
          service="redis"
          icon={<Memory />}
          delay={100}
        />
      </Grid>
    </Grid>
  );
}
